import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Check,
    ArrowUpRight,
    CircuitBoard,
    SunMedium,
    Music2,
    ShieldCheck,
    Network,
    Building2,
} from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import BackLink from "../components/BackLink";
import SEO from "../components/SEO";
import StructuredData, { serviceSchema } from "../components/StructuredData";
import GoldButton from "../components/GoldButton";
import Picture from "../components/Picture";
import CTABand from "../components/sections/CTABand";
import { SERVICES, BRAND } from "../data/content";

const ICONS = {
    CircuitBoard,
    SunMedium,
    Music2,
    ShieldCheck,
    Network,
    Building2,
};

// Keyword-optimized SEO overrides per service slug. Falls back to
// `service.name` / `service.short` when a slug isn't listed. Titles kept
// ≤60 chars for Google's SERP truncation; descriptions ≤155 chars.
const SEO_BY_SLUG = {
    "home-automation": {
        title: "Home Automation Integration — Control4 & Savant Certified",
        description:
            "Whole-home automation for luxury residences. Control4, Savant, and Lutron platforms unified into a single intuitive experience.",
    },
    "lighting-shades": {
        title: "Luxury Lighting & Motorized Shades — Lutron Certified",
        description:
            "Lutron HomeWorks and Ketra lighting with silent motorized shades. Circadian tuning and architectural scenes for estate residences.",
    },
    "home-theater-audio": {
        title: "High-End Home Theater & Distributed Audio Design",
        description:
            "Reference-grade cinema and multi-room audio integration. Discreet, calibrated, and tuned to the room's architecture.",
    },
    "security-surveillance": {
        title: "Estate Security & Surveillance Systems",
        description:
            "Discreet estate surveillance, access control, and monitored intrusion — integrated with lighting and automation.",
    },
    "networking-wifi": {
        title: "Estate Networking & Enterprise Wi-Fi — Ubiquiti Certified",
        description:
            "Enterprise-grade networking, seamless Wi-Fi 6E/7 coverage, and fiber backbone for multi-acre estates and yachts.",
    },
    commercial: {
        title: "Commercial AV & Multi-Estate Integration",
        description:
            "Executive offices, hospitality, superyachts, and multi-residence portfolios — matched to the quality of a private residence.",
    },
};

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);

    if (!service) {
        return <Navigate to="/services" replace />;
    }

    const Icon = ICONS[service.icon] ?? CircuitBoard;
    const currentIdx = SERVICES.findIndex((s) => s.slug === slug);
    const nextService =
        SERVICES[(currentIdx + 1) % SERVICES.length];

    const seoOverride = SEO_BY_SLUG[service.slug] || {};

    return (
        <div data-testid={`service-detail-${service.slug}`}>
            <SEO
                title={seoOverride.title || service.name}
                description={seoOverride.description || service.short}
                canonical={`https://actluxe.net/services/${service.slug}`}
                lcpSlug={service.hero}
                lcpSizes="100vw"
            />
            <StructuredData data={serviceSchema(service)} />
            {/* Hero banner */}
            <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden bg-[#0B0B0C]">
                <Picture
                    slug={service.hero}
                    alt={`${service.name} — signature ACT service`}
                    priority
                    sizes="100vw"
                    className="absolute inset-0 h-full w-full object-cover animate-ken-burns"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-[#0B0B0C]/55 to-[#0B0B0C]" />

                <div className="relative h-full flex items-end pb-20 md:pb-24">
                    <div className="shell w-full">
                        <motion.div
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="max-w-3xl"
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <span className="eyebrow">
                                    {service.number} — Service
                                </span>
                                <span className="h-px w-10 bg-[#C9A86A]/50" />
                                <Icon
                                    size={20}
                                    strokeWidth={1.1}
                                    className="text-[#C9A86A]"
                                />
                            </div>
                            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#F3EFE7] leading-[1.05]">
                                {service.name}
                            </h1>
                            <p className="mt-6 text-lg md:text-xl text-[#C9A86A] font-serif italic">
                                {service.tagline}
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Return-to-parent breadcrumb — placed under the hero on all
                deep pages for consistent navigation back up the hierarchy. */}
            <div className="shell pt-6 md:pt-8 pb-2">
                <BackLink to="/services" label="All services" />
            </div>

            {/* Intro */}
            <section className="section-pad bg-[#0B0B0C]">
                <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-4">
                        <SectionLabel number="—" label="Practice" />
                        <p className="mt-6 font-serif text-2xl md:text-3xl text-[#F3EFE7] leading-[1.25]">
                            An ACT signature, delivered at the standard of the
                            residence itself.
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <p className="text-[#9A958A] text-lg md:text-[19px] leading-[1.9]">
                            {service.intro}
                        </p>
                    </div>
                </div>
            </section>

            {/* What's Included */}
            <section className="section-pad bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.12)]">
                <div className="shell">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-end mb-12 md:mb-16">
                        <div className="lg:col-span-7">
                            <SectionLabel number="01" label="What's Included" />
                            <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
                                Delivered as{" "}
                                <span className="italic text-[#C9A86A]">
                                    one considered package.
                                </span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                        {service.included.map((item, idx) => (
                            <motion.div
                                key={item}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.6,
                                    delay: idx * 0.05,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="flex gap-5 py-5 border-b border-[rgba(201,168,106,0.12)]"
                                data-testid={`service-included-${idx}`}
                            >
                                <span className="flex-none inline-flex items-center justify-center w-8 h-8 border border-[#C9A86A]/60 text-[#C9A86A]">
                                    <Check size={14} strokeWidth={1.6} />
                                </span>
                                <p className="text-[#F3EFE7] text-base md:text-[17px] leading-[1.75]">
                                    {item}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Approach */}
            <section className="section-pad bg-[#15161A] border-t border-[rgba(201,168,106,0.12)]">
                <div className="shell">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-end mb-16 md:mb-20">
                        <div className="lg:col-span-7">
                            <SectionLabel number="02" label="Our Approach" />
                            <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
                                Three unhurried steps,{" "}
                                <span className="italic text-[#C9A86A]">
                                    each on paper first.
                                </span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(201,168,106,0.15)] border border-[rgba(201,168,106,0.15)]">
                        {service.approach.map((step, idx) => (
                            <motion.div
                                key={step.step}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.7,
                                    delay: idx * 0.08,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="bg-[#15161A] p-10 md:p-12 transition-colors duration-500 hover:bg-[#1C1D22]"
                                data-testid={`service-approach-${idx}`}
                            >
                                <div className="flex items-center justify-between mb-8">
                                    <span className="font-serif text-5xl md:text-6xl text-[#C9A86A] leading-none">
                                        0{idx + 1}
                                    </span>
                                    <span className="text-[10px] tracking-[0.32em] uppercase text-[#9A958A]">
                                        Step {idx + 1}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl text-[#F3EFE7] mb-4">
                                    {step.step}
                                </h3>
                                <p className="text-[#9A958A] text-[15px] leading-[1.85]">
                                    {step.copy}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Imagery block */}
            <section className="bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.12)]">
                <div className="shell py-16 md:py-24">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                        <motion.div
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="md:col-span-8 relative overflow-hidden aspect-[4/3] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full"
                        >
                            <Picture
                                slug={service.imagery[0]}
                                alt={`${service.name} — detail.`}
                                sizes="(max-width: 768px) 100vw, 65vw"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        </motion.div>
                        <motion.div
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="md:col-span-4 relative overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[320px] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full"
                        >
                            <Picture
                                slug={service.imagery[1] || service.imagery[0]}
                                alt={`${service.name} — context.`}
                                sizes="(max-width: 768px) 100vw, 35vw"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA + next service */}
            <section className="section-pad bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.15)]">
                <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-7">
                        <SectionLabel number="—" label="Next Step" />
                        <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08] max-w-2xl">
                            A private conversation about{" "}
                            <span className="italic text-[#C9A86A]">
                                {service.name.toLowerCase()}.
                            </span>
                        </h2>
                        <p className="mt-8 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-xl">
                            Tell us about the residence. We&rsquo;ll respond
                            personally, and arrange a quiet, no-obligation
                            introduction at your convenience.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <GoldButton
                                to="/contact"
                                testId={`service-cta-contact-${service.slug}`}
                            >
                                Book a Consultation
                            </GoldButton>
                            <GoldButton
                                href={BRAND.phoneHref}
                                variant="outline"
                                testId={`service-cta-call-${service.slug}`}
                            >
                                Call {BRAND.phone}
                            </GoldButton>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="border-t border-[rgba(201,168,106,0.25)] pt-10">
                            <span className="eyebrow">Next Service</span>
                            <Link
                                to={`/services/${nextService.slug}`}
                                className="group mt-4 block"
                                data-testid={`service-next-${nextService.slug}`}
                            >
                                <div className="flex items-center justify-between gap-6">
                                    <h3 className="font-serif text-3xl md:text-4xl text-[#F3EFE7] leading-[1.1] transition-colors duration-500 group-hover:text-[#D9BC80]">
                                        {nextService.name}
                                    </h3>
                                    <span className="flex-none inline-flex items-center justify-center w-12 h-12 border border-[#C9A86A]/50 text-[#C9A86A] transition-all duration-500 ease-out group-hover:bg-[#C9A86A] group-hover:text-[#0B0B0C]">
                                        <ArrowUpRight
                                            size={18}
                                            strokeWidth={1.1}
                                        />
                                    </span>
                                </div>
                                <p className="mt-4 text-[#9A958A] text-sm leading-[1.85]">
                                    {nextService.short}
                                </p>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <CTABand />
        </div>
    );
}
